import dynamic from 'next/dynamic';
import React, { Suspense } from 'react';
import Meta from '../components/Meta';

const MapSkeleton = () => (
  <div className="flex h-full w-full items-center justify-center bg-[var(--c-bg)] neo-civic-grid">
    <div className="relative flex flex-col items-center gap-4">
      <div className="relative flex h-16 w-16 items-center justify-center rounded-lg border border-[var(--c-border)] bg-[var(--c-surface)] shadow-lg">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-transparent border-t-[var(--c-accent)]" />
      </div>
      <div className="text-center">
        <p className="text-sm font-medium text-[var(--c-text-primary)] font-['Space_Grotesk']">
          Loading Tram Plan
        </p>
        <p className="mt-1 text-xs uppercase tracking-wide text-[var(--c-text-secondary)] font-['DM_Mono']">
          Initializing Transit View
        </p>
      </div>
    </div>
  </div>
);

const TramPlanMap = dynamic(() => import('../components/tram/TramPlanMap'), {
  ssr: false,
  loading: () => <MapSkeleton />,
});

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(err, info) {
    console.error('TramPlanMap encountered an error', err, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex h-full w-full flex-col items-center justify-center gap-6 bg-[var(--c-bg)] p-8 text-center">
          <div className="max-w-md space-y-4">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-lg border border-[var(--c-border)] bg-[var(--c-surface)]">
              <svg
                className="h-8 w-8 text-[var(--c-error)]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <div className="space-y-2">
              <h2 className="text-xl font-bold text-[var(--c-text-primary)] font-['Space_Grotesk']">
                Map Loading Error
              </h2>
              <p className="text-sm leading-relaxed text-[var(--c-text-secondary)]">
                The tram plan map encountered an issue during initialization. Please refresh the
                page and try again.
              </p>
            </div>
            <button
              onClick={() => location.assign(location.href)}
              className="rounded-md bg-[var(--c-accent)] px-4 py-2 text-sm font-semibold text-[var(--c-bg)] transition-colors hover:bg-[var(--c-accent)]/90"
            >
              Reload Map
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default function TramPlanPage({ linesData, stationsData, bounds }) {
  const cityKey = 'edi';

  return (
    <>
      <Meta
        title="Edinburgh Tram Map and Tram Plan | City Scope"
        description="Explore City Scope's interactive Edinburgh tram map and tram plan."
        canonical="/edi-tram-plan"
      />

      <div
        className="relative h-screen w-full overflow-hidden bg-[var(--c-bg)]"
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        <Suspense fallback={<MapSkeleton />}>
          <ErrorBoundary>
            <TramPlanMap
              cityKey={cityKey}
              linesData={linesData}
              stationsData={stationsData}
              bounds={bounds}
            />
          </ErrorBoundary>
        </Suspense>
      </div>
    </>
  );
}

export async function getStaticProps() {
  return {
    props: {
      linesData: null,
      stationsData: null,
      bounds: null,
    },
    revalidate: 60 * 60 * 12,
  };
}
